import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { processService } from '~/services/processAPI';
import { CreateProcessWebhookInput } from '~/services/webhooksAPI/webhooksService';
import { getProcessActivitiesQueryKey } from '../activity';
import {
  formatErrorPayload,
  UseApiMutationOptions,
  UseApiQueryOptions,
  validateApiResponse,
} from '../utils';
import { useCurrentWorkspaceQueryKey } from '../utils/useQueryKey';
import { EditProcessInput } from '../../services/processAPI/processService';

const ROOT_KEY = ['processes'];
const getQueryKey = (processId: string) => [...ROOT_KEY, processId];

export const useProcessesQuery = (options?: UseApiQueryOptions<typeof processService.getAll>) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(ROOT_KEY),
    async () => validateApiResponse(await processService.getAll({ workspaceId })).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useProcessQuery = (
  processId: string,
  options?: UseApiQueryOptions<typeof processService.getProcessById>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getQueryKey(processId),
    async () =>
      validateApiResponse(await processService.getProcessById({ workspaceId, processId })).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useCreateProcessMutation = (
  options?: UseApiMutationOptions<typeof processService.create>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      name,
      explicitOutput,
      description,
      webhookConfigs,
      disabledState,
    }: {
      name: string;
      explicitOutput: boolean;
      description: string | null;
      webhookConfigs: CreateProcessWebhookInput[];
      disabledState?: { reason: null | string } | null;
    }) =>
      validateApiResponse(
        await processService.create({
          workspaceId,
          name,
          explicitOutput,
          webhookConfigs,
          description,
          disabledState,
        }),
      ).data,
    {
      onSuccess: (process) => {
        if (process) {
          queryClient.setQueryData(getQueryKey(process.id), process);
          queryClient.setQueryData(queryKeyFormatter(ROOT_KEY), (processes: unknown[] = []) => [
            ...processes,
            process,
          ]);
        }
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useEditProcessMutation = <T extends typeof processService.editProcessById>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId, queryKeyFormatter } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, process }: { processId: string; process: EditProcessInput }) =>
      validateApiResponse(await processService.editProcessById({ workspaceId, processId, process }))
        .data,
    {
      onSuccess: (data, { processId, process }) => {
        queryClient.setQueryData(getQueryKey(processId), process);
        if (data) {
          queryClient.setQueriesData(
            queryKeyFormatter(ROOT_KEY),
            (previous: (typeof data)[] = []) =>
              previous.map((previousData) => (previousData.id === processId ? data : previousData)),
          );
        }
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useDeleteProcessMutation = (
  options?: UseApiMutationOptions<typeof processService.deleteOneById>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId, queryKeyFormatter } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId }: { processId: string }) =>
      validateApiResponse(await processService.deleteOneById({ workspaceId, id: processId })).data,
    {
      onSuccess: (data, { processId }) => {
        queryClient.removeQueries(getQueryKey(processId));
        if (data) {
          queryClient.setQueriesData(
            queryKeyFormatter(ROOT_KEY),
            (previous: (typeof data)[] = []) =>
              previous.filter((previousProcess) => previousProcess.id !== processId),
          );
        }
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useUpdateProcessActivityOrderMutation = <
  T extends typeof processService.updateProcessActivityOrder,
>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, order }: { processId: string; order: Record<number, string> }) =>
      validateApiResponse(
        await processService.updateProcessActivityOrder({ workspaceId, processId, order }),
      ).data,
    {
      onSuccess: (data, { processId }) => {
        queryClient.setQueriesData(getProcessActivitiesQueryKey(processId), data);
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useGenerateProcessSecretMutation = <
  T extends typeof processService.generateProcessSecret,
>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId }: { processId: string }) =>
      validateApiResponse(await processService.generateProcessSecret({ workspaceId, processId }))
        .data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useDeleteProcessSecretMutation = <T extends typeof processService.deleteProcessSecret>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId }: { processId: string }) =>
      validateApiResponse(await processService.deleteProcessSecret({ workspaceId, processId }))
        .data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};
